import { IconButton, TableCell, TableRow } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import QueryStatusChip from './QueryStatusChip'
import SubjectStatusChip from './SubjectStatusChip'
import { TimepointSubject } from '@common/config/api/client'
import { generatePathWithParams } from '@common/utils/urlUtils'
import { ROUTES } from 'src/routes'
import { useNavigate, useParams, createSearchParams } from 'react-router-dom'

export default function SubjectTableRow({
  subject,
  expanded,
  setExpanded,
  limit,
  previous_cursor,
  next_cursor,
}: {
  subject: TimepointSubject
  expanded: boolean
  setExpanded: (expanded: boolean) => void
  limit?: number
  previous_cursor?: string | null | undefined
  next_cursor?: string | null | undefined
}) {
  const { studyId } = useParams<{ studyId: string }>()
  const navigate = useNavigate()

  const navigateToSubjectDetails = () => {
    const searchParams = {
      limit: (limit || 50).toString(),
      previous_cursor: previous_cursor || '',
      next_cursor: next_cursor || '',
    }
    const path = generatePathWithParams(ROUTES.SUBJECT_OVERVIEW.path, {
      studyId: studyId!,
      subjectId: subject.subject_id,
    })
    navigate({ pathname: path, search: `?${createSearchParams(searchParams)}` })
  }

  return (
    <TableRow
      data-testid="subject-table-row"
      hover
      onClick={navigateToSubjectDetails}
      sx={{
        '&:hover': {
          '& > td': {
            backgroundColor: (theme) => theme.palette.action.hover,
          },
        },
      }}
    >
      <TableCell
        padding="checkbox"
        sx={{
          width: '20%',
          minWidth: 48,
          boxSizing: 'border-box',
          tableLayout: 'fixed',
          position: 'sticky',
          left: 0,
          bgcolor: 'background.default',
          zIndex: 30,
        }}
        data-testid="expand-cell"
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            setExpanded(!expanded)
          }}
          data-testid="expand-button"
        >
          {expanded ? (
            <KeyboardArrowUpIcon data-testid="icon-expanded" />
          ) : (
            <KeyboardArrowDownIcon data-testid="icon-collapsed" />
          )}
        </IconButton>
      </TableCell>
      <TableCell
        sx={{
          position: 'sticky',
          left: 48,
          bgcolor: 'background.default',
        }}
        data-testid="pseudo-id-cell"
      >
        {subject.pseudo_id}
      </TableCell>
      <TableCell
        sx={{
          position: 'sticky',
          left: 168, // 48 + 120
          bgcolor: 'background.default',
        }}
        data-testid="blind-code-cell"
      >
        {subject.blind_code || '-'}
      </TableCell>
      <TableCell
        sx={{
          position: 'sticky',
          left: 288,
          bgcolor: 'background.default',
        }}
        data-testid="site-id-cell"
      >
        {subject.study_site_pseudo_id}
      </TableCell>
      <TableCell
        sx={{
          position: 'sticky',
          left: 408,
          bgcolor: 'background.default',
        }}
        data-testid="status-chip-cell"
      >
        <SubjectStatusChip subjectStatus={subject.status} size="small" />
      </TableCell>
      <TableCell
        sx={{
          position: 'sticky',
          left: 528,
          bgcolor: 'background.default',
        }}
        data-testid="query-chip-cell"
      >
        <QueryStatusChip queryStatus={subject.query_status as string} />
      </TableCell>
      {subject.study_timepoints?.map((studyTimepoint) => (
        <TableCell
          key={studyTimepoint.timepoint_id}
          data-testid={`timepoint-cell-${studyTimepoint.timepoint_id}`}
          sx={{
            minWidth: 220,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {studyTimepoint.timepoint_submissions?.length} Timepoints
        </TableCell>
      ))}
    </TableRow>
  )
}
